/**
 * Preços dos eBooks/curso no FRONTEND — espelho do catálogo do servidor
 * (cantate-api → ebookProducts.ts). PIX e cartão têm preços diferentes e cada
 * produto tem um limite de parcelas.
 *
 * ⚠️ O valor real cobrado é SEMPRE decidido no backend. Este módulo serve só
 * para EXIBIR os preços (PIX vs cartão) e calcular parcelas no checkout.
 *
 *   Individual (qualquer eBook avulso): PIX 17,90 · Cartão 19,90 · 1x
 *   Fingerstyle (tablaturas):           PIX 39,90 · Cartão 47,00 · 3x
 *   Completo (coleção 13 em 1):         PIX 79,90 · Cartão 97,00 · 6x
 *   Combo eBook (Completo+Fingerstyle): PIX 99,00 · Cartão 119,90 · 6x
 *   Curso:                              PIX 147,00 · Cartão 197,00 · 12x
 *   Curso + eBook (Curso+Fingerstyle):  PIX 186,90 · Cartão 247,00 · 12x
 */

const COURSE_ID = 'curso-fingerstyle-catolico';
const FINGERSTYLE_ID = 'tablaturas';
const COMPLETO_ID = 'colecao-completa-13-1';

function kindOf(itemId) {
  if (itemId === COURSE_ID) return 'curso';
  if (itemId === FINGERSTYLE_ID) return 'fingerstyle';
  if (itemId === COMPLETO_ID) return 'completo';
  return 'individual';
}

// Preço unitário (centavos) por tipo/método + parcelas máximas.
const UNIT = {
  individual: { pix: 1790, card: 1990, maxInstallments: 1 },
  fingerstyle: { pix: 3990, card: 4700, maxInstallments: 3 },
  completo: { pix: 7990, card: 9700, maxInstallments: 6 },
  curso: { pix: 14700, card: 19700, maxInstallments: 12 },
};

// Combos "fechados".
const COMBOS = [
  { kinds: ['completo', 'fingerstyle'], pix: 9900, card: 11990, maxInstallments: 6 },
  { kinds: ['curso', 'fingerstyle'], pix: 18690, card: 24700, maxInstallments: 12 },
];

function sameKinds(a, b) {
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((k, i) => k === sb[i]);
}

/** Preço unitário (centavos) de um item, por método. */
export function unitPriceCents(itemId, method) {
  const u = UNIT[kindOf(itemId)];
  return method === 'pix' ? u.pix : u.card;
}

/**
 * Calcula o total (centavos) + parcelas máximas do carrinho para o método.
 * @returns {{ totalCents:number, amount:number, maxInstallments:number,
 *            itens: {itemId:string,valor:number}[] }}
 */
export function priceCart(items, method) {
  const list = Array.isArray(items) ? items : [];
  if (list.length === 0) {
    return { totalCents: 0, amount: 0, maxInstallments: method === 'pix' ? 1 : 12, itens: [] };
  }
  const ids = list.map((i) => i.id || i.itemId);
  const kinds = ids.map(kindOf);

  const combo = COMBOS.find((c) => sameKinds(c.kinds, kinds));
  if (combo) {
    const totalCents = method === 'pix' ? combo.pix : combo.card;
    const per = Math.round(totalCents / list.length);
    const itens = ids.map((itemId, idx) => ({
      itemId,
      valor: idx === list.length - 1 ? totalCents - per * (list.length - 1) : per,
    }));
    return { totalCents, amount: totalCents / 100, maxInstallments: combo.maxInstallments, itens };
  }

  let totalCents = 0;
  let maxInstallments = 12;
  const itens = ids.map((itemId) => {
    const u = UNIT[kindOf(itemId)];
    const valor = method === 'pix' ? u.pix : u.card;
    totalCents += valor;
    maxInstallments = Math.min(maxInstallments, u.maxInstallments);
    return { itemId, valor };
  });
  return {
    totalCents,
    amount: totalCents / 100,
    maxInstallments: method === 'pix' ? 1 : maxInstallments,
    itens,
  };
}

export const brl = (cents) =>
  `R$ ${(Number(cents || 0) / 100).toFixed(2).replace('.', ',')}`;
